import { capitalizeFirstWord, timelineTimeKey } from '@peles/shared';
import type { TimelineEntry, UpdateTimelineEntryInput } from '@peles/shared';

/** What the entry form holds — every field as typed, `time` as `HH:MM` for `<input type="time">`. */
export interface EntryFormState {
  time: string;
  title: string;
  location: string;
  vendorNote: string;
  notes: string;
}

export const BLANK_ENTRY: EntryFormState = {
  time: '',
  title: '',
  location: '',
  vendorNote: '',
  notes: '',
};

/**
 * A time as the time picker holds it: `HH:MM`, or '' when it reads as no
 * time of day ("After the ceremony" from an import). Re-exported for the
 * pages' existing callers.
 */
export function timeToInputValue(time: string | null | undefined): string {
  return timelineTimeKey(time) ?? '';
}

export function entryToForm(entry: TimelineEntry): EntryFormState {
  return {
    time: timeToInputValue(entry.time),
    title: entry.title,
    location: entry.location ?? '',
    vendorNote: entry.vendorNote ?? '',
    notes: entry.notes ?? '',
  };
}

/** The new entry's fields; blanks are left out. */
export function newEntryInput(form: EntryFormState) {
  return {
    time: form.time || undefined,
    title: capitalizeFirstWord(form.title.trim()),
    location: form.location.trim() || undefined,
    vendorNote: form.vendorNote.trim() || undefined,
    notes: form.notes.trim() || undefined,
  };
}

/**
 * Only the fields the producer changed. A time the picker could not show
 * ("After the ceremony") opens blank; sending that blank back would wipe a
 * time nobody touched, so an untouched field is never sent.
 */
export function changedEntryFields(
  initial: EntryFormState,
  form: EntryFormState,
): UpdateTimelineEntryInput {
  const updates: UpdateTimelineEntryInput = {};
  if (form.time !== initial.time) updates.time = form.time || null;
  if (form.title.trim() !== initial.title.trim()) {
    updates.title = capitalizeFirstWord(form.title.trim());
  }
  if (form.location.trim() !== initial.location.trim()) {
    updates.location = form.location.trim() || null;
  }
  if (form.vendorNote.trim() !== initial.vendorNote.trim()) {
    updates.vendorNote = form.vendorNote.trim() || null;
  }
  if (form.notes.trim() !== initial.notes.trim()) updates.notes = form.notes.trim() || null;
  return updates;
}
